import { zodResolver } from '@hookform/resolvers/zod';
import {
  PaymentGatewayAccountUnlinkReq,
  PaymentGatewayAccountUnlinkSchema,
  PaymentGatewayAccountUnlinkUI,
} from '@monetize/types/app';
import { FilterTypeOperator } from '@monetize/types/common';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUnlinkPaymentGatewayAccount } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import {
  MAlert,
  MButton,
  MCenterModal,
  MStack,
} from '../../../components/Monetize';
import { useAccountPayments } from '../../../hooks/AccountDetail/useAccountPayments';
import { logger } from '../../../services/logger';
import { GatewayAccountUnlinkForm } from './GatewayAccountUnlinkForm';

interface GatewayAccountUnlinkModalProps {
  onClose: () => void;
  accountId: string;
  paymentGatewayId: string;
  gatewayCustomerId: string;
}

export const GatewayAccountUnlinkModal: React.FC<
  GatewayAccountUnlinkModalProps
> = ({ onClose, accountId, paymentGatewayId, gatewayCustomerId }) => {
  const {
    setFilters,
    totalRecords: paymentCount,
    loading: paymentsLoading,
  } = useAccountPayments({
    accountId,
    queryOptions: {
      enabled: true,
    },
  });

  useEffect(() => {
    setFilters([
      {
        key: 'paymentGatewayId',
        operator: FilterTypeOperator.EQUAL,
        value: paymentGatewayId,
      },
      {
        key: 'gatewayCustomerId',
        operator: FilterTypeOperator.EQUAL,
        value: gatewayCustomerId,
      },
    ]);
  }, [paymentGatewayId, gatewayCustomerId, accountId, setFilters]);

  const { control, handleSubmit } = useForm<PaymentGatewayAccountUnlinkUI>({
    resolver: zodResolver(PaymentGatewayAccountUnlinkSchema),
    mode: 'onSubmit',
  });

  const { mutateAsync: doUnlinkPaymentGatewayAccount, isPending: isLoading } =
    useUnlinkPaymentGatewayAccount();

  const onUnlinkPaymentGatewayAccountSubmit = async (
    formData: PaymentGatewayAccountUnlinkReq,
  ) => {
    try {
      await doUnlinkPaymentGatewayAccount({
        payload: formData,
        accountId,
        paymentGatewayId,
        gatewayCustomerId,
      });
      onClose();
    } catch (error) {
      logger.error('Unlink payment gateway account Error', error);
      handleApiErrorToast(error);
    }
  };

  const onUnlinkPaymentGatewayAccountError = (error: any) => {
    logger.error('Unlink payment gateway account Error ', error);
  };

  return (
    <MCenterModal
      isOpen
      onClose={onClose}
      modalTitle="Are you sure you want to unlink this gateway account?"
      size="lg"
      isLoading={paymentsLoading}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>

          <MButton
            variant="delete"
            minW="auto"
            isLoading={isLoading}
            isDisabled={isLoading || paymentsLoading}
            onClick={handleSubmit(
              onUnlinkPaymentGatewayAccountSubmit,
              onUnlinkPaymentGatewayAccountError,
            )}
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      {paymentCount > 0 && (
        <MAlert type="warning" mb={4}>
          There are existing payments associated with this customer. After you
          unlink, these payments will no longer be eligible for refunds within
          MonetizeNow.
        </MAlert>
      )}
      <GatewayAccountUnlinkForm control={control} />
    </MCenterModal>
  );
};
